import { useState, useEffect } from 'react'

export type EffectiveConnectionType = 'slow-2g' | '2g' | '3g' | '4g' | 'unknown'

type NetworkInformation = {
  effectiveType?: string
  downlink?: number
  rtt?: number
  saveData?: boolean
  addEventListener?: (type: string, listener: () => void) => void
  removeEventListener?: (type: string, listener: () => void) => void
}

const getConnection = (): NetworkInformation | undefined => {
  if (typeof navigator === 'undefined') return undefined
  const nav = navigator as Navigator & {
    connection?: NetworkInformation
    mozConnection?: NetworkInformation
    webkitConnection?: NetworkInformation
  }
  return nav.connection || nav.mozConnection || nav.webkitConnection
}

const normalizeConnectionType = (effectiveType?: string): EffectiveConnectionType => {
  const normalized = (effectiveType || '').toLowerCase()
  return ['slow-2g', '2g', '3g', '4g'].includes(normalized)
    ? (normalized as EffectiveConnectionType)
    : 'unknown'
}

const useNetworkStatus = () => {
  const [isOnline, setOnline] = useState(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true
  )
  const [effectiveType, setEffectiveType] = useState<EffectiveConnectionType>('unknown')
  const [downlink, setDownlink] = useState<number | undefined>()
  const [rtt, setRtt] = useState<number | undefined>()
  const [saveData, setSaveData] = useState(false)

  useEffect(() => {
    const conn = getConnection()

    const applyConnectionDetails = () => {
      if (!conn) return
      setEffectiveType(normalizeConnectionType(conn.effectiveType))
      setDownlink(conn.downlink)
      setRtt(conn.rtt)
      setSaveData(Boolean(conn.saveData))
    }

    const updateNetworkStatus = () => {
      setOnline(typeof navigator !== 'undefined' ? navigator.onLine : true)
      applyConnectionDetails()
    }

    applyConnectionDetails()
    window.addEventListener('online', updateNetworkStatus)
    window.addEventListener('offline', updateNetworkStatus)
    conn?.addEventListener?.('change', applyConnectionDetails)

    return () => {
      window.removeEventListener('online', updateNetworkStatus)
      window.removeEventListener('offline', updateNetworkStatus)
      conn?.removeEventListener?.('change', applyConnectionDetails)
    }
  }, [])

  return { isOnline, effectiveType, downlink, rtt, saveData }
}

export { useNetworkStatus }
